import { LitElement, html, css } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import '../katapult-elements/katapult-dialog.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import { KatapultButton } from '../katapult-elements/katapult-button.js';
import { serialize } from '@shoelace-style/shoelace';
import { ServerRequest } from '../../modules/ServerRequest.js';
import { stripIndent } from 'common-tags';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';

/** @import { Ref } from 'lit/directives/ref.js' */

const KATAPULT_SUPPORT_EMAIL = 'support@katapultengineering.com';

class RequestServerAccessDialog extends LitElement {
  static styles = css`
    :host {
      display: contents;
    }
  `;

  constructor() {
    super();

    /** @type {Ref<KatapultDialog>} */
    this._dialogRef = createRef();
    /** @type {Ref<HTMLFormElement>} */
    this._formRef = createRef();
    /** @type {Ref<KatapultButton>} */
    this._submitButtonRef = createRef();
  }

  get closeComplete() {
    return this._dialogRef.value?.closeComplete;
  }

  render() {
    return html`
      <katapult-dialog
        ${ref(this._dialogRef)}
        width="600"
        opened
        title="Request Server Access"
        @closed=${() => this._formRef.value?.reset()}
        icon="how_to_reg"
      >
        <form ${ref(this._formRef)} @submit=${(e) => e.preventDefault()}>
          <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium);">
            <div style="color: var(--sl-color-neutral-700)">Request access to this server as an attacher or contractor company.</div>
            <div style="color: var(--sl-color-neutral-700)">
              A server administrator will review your request, and may reach out to you for additional information.
            </div>
            <sl-input placeholder="Name" name="name" autocomplete="name" required></sl-input>
            <sl-input type="email" placeholder="Email" name="email" autocomplete="email" required></sl-input>
            <sl-input placeholder="Company" name="company" autocomplete="organization" required></sl-input>
            <sl-textarea
              placeholder="Request Details"
              help-text="Describe your server access requirements. What is your intended scope of work?"
              name="message"
              required
            ></sl-textarea>
          </div>
        </form>
        <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
        <katapult-button ${ref(this._submitButtonRef)} slot="buttons" color="var(--sl-color-primary)" @click=${this._submitForm.bind(this)}
          >Submit</katapult-button
        >
      </katapult-dialog>
    `;
  }

  async _submitForm() {
    const dialog = this._dialogRef.value;
    if (!dialog) throw new Error('Dialog not found');

    const submitButton = this._submitButtonRef.value;
    if (!submitButton) throw new Error('Submit button not found');

    const form = this._formRef.value;
    if (!form) throw new Error('Form not found');

    try {
      const serverOwnerEmail = globalThis.config.firebaseData?.server_owner_email ?? KATAPULT_SUPPORT_EMAIL;

      const formIsValid = form.reportValidity();
      if (!formIsValid) throw new Error('Form is invalid');

      const formData = serialize(form);

      const emailConfig = {
        addresses: [serverOwnerEmail],
        subject: `Server Access Request from ${formData.name} at ${formData.company}`,
        body: stripIndent`
          ${formData.name} from ${formData.company} has requested access to the server:

          Name: ${formData.name}
          Email: ${formData.email}
          Company: ${formData.company}
          Message: ${formData.message}
        `
      };

      submitButton.loading = true;

      await ServerRequest('email', emailConfig);
      submitButton.showSuccess();
      setTimeout(() => dialog.close(), 2000);
    } catch (error) {
      submitButton.showError();
      throw error;
    } finally {
      submitButton.loading = false;
    }
  }
}
customElements.define('request-server-access-dialog', RequestServerAccessDialog);

export function RequestServerAccess() {
  const serverOwnerEmail = globalThis.config.firebaseData?.server_owner_email ?? KATAPULT_SUPPORT_EMAIL;
  KatapultDialog.alert({
    dialog: {
      title: 'Request Server Access',
      icon: 'how_to_reg'
    },
    template: () => html`
      <div
        style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium); padding: var(--sl-spacing-small); color: var(--sl-color-neutral-900); text-align: center;"
      >
        <div>This server is invite-only</div>
        <div>
          If you need access, please contact the server owner at
          <a style="color: var(--sl-color-primary);" href="mailto:${serverOwnerEmail}">${serverOwnerEmail}</a>
        </div>
      </div>
    `
  });
}
